/**
 * JavaScript Autocomplete for CodeMirror 6
 *
 * Provides IntelliSense for:
 * - ECMAScript global objects and their methods (JSON, Math, Date, Array, Object, etc.)
 * - workflowContext object with dynamic action names from the workflow
 * - Common output properties (body, headers, statusCode)
 */

import { autocompletion, type CompletionContext, type Completion } from '@codemirror/autocomplete';
import { EditorView } from '@codemirror/view';
import type { Extension } from '@codemirror/state';

// =============================================================================
// ECMASCRIPT GLOBAL COMPLETIONS
// =============================================================================

/** Top-level global objects and functions */
const globalCompletions: Completion[] = [
  // Global objects
  { label: 'JSON', type: 'class', info: 'JSON parsing and serialization' },
  { label: 'Math', type: 'class', info: 'Mathematical constants and functions' },
  { label: 'Date', type: 'class', info: 'Date and time handling' },
  { label: 'Array', type: 'class', info: 'Array constructor and utilities' },
  { label: 'Object', type: 'class', info: 'Object constructor and utilities' },
  { label: 'String', type: 'class', info: 'String constructor and utilities' },
  { label: 'Number', type: 'class', info: 'Number constructor and utilities' },
  { label: 'Boolean', type: 'class', info: 'Boolean constructor' },
  { label: 'RegExp', type: 'class', info: 'Regular expression constructor' },
  { label: 'Error', type: 'class', info: 'Error constructor' },
  { label: 'TypeError', type: 'class', info: 'Type error constructor' },
  { label: 'Promise', type: 'class', info: 'Promise constructor for async operations' },
  { label: 'Map', type: 'class', info: 'Key-value map collection' },
  { label: 'Set', type: 'class', info: 'Unique value collection' },

  // Global functions
  { label: 'parseInt', type: 'function', info: 'Parse string to integer', detail: '(string, radix?)' },
  { label: 'parseFloat', type: 'function', info: 'Parse string to float', detail: '(string)' },
  { label: 'isNaN', type: 'function', info: 'Check if value is NaN', detail: '(value)' },
  { label: 'isFinite', type: 'function', info: 'Check if value is finite', detail: '(value)' },
  { label: 'encodeURIComponent', type: 'function', info: 'Encode URI component', detail: '(string)' },
  { label: 'decodeURIComponent', type: 'function', info: 'Decode URI component', detail: '(string)' },
  { label: 'encodeURI', type: 'function', info: 'Encode URI', detail: '(string)' },
  { label: 'decodeURI', type: 'function', info: 'Decode URI', detail: '(string)' },

  // Constants
  { label: 'undefined', type: 'keyword', info: 'Undefined value' },
  { label: 'null', type: 'keyword', info: 'Null value' },
  { label: 'NaN', type: 'keyword', info: 'Not a Number' },
  { label: 'Infinity', type: 'keyword', info: 'Infinity value' },
  { label: 'true', type: 'keyword' },
  { label: 'false', type: 'keyword' },

  // Console
  { label: 'console', type: 'variable', info: 'Console output' },

  // Workflow context (Logic Apps runtime)
  { label: 'workflowContext', type: 'variable', info: 'Access to workflow trigger and action outputs' },
];

/** Static member completions for built-in objects */
const staticMemberCompletions: Record<string, Completion[]> = {
  JSON: [
    { label: 'parse', type: 'function', info: 'Parse JSON string to object', detail: '(text, reviver?)' },
    { label: 'stringify', type: 'function', info: 'Convert object to JSON string', detail: '(value, replacer?, space?)' },
  ],
  Math: [
    { label: 'abs', type: 'function', info: 'Absolute value', detail: '(x)' },
    { label: 'ceil', type: 'function', info: 'Round up to integer', detail: '(x)' },
    { label: 'floor', type: 'function', info: 'Round down to integer', detail: '(x)' },
    { label: 'round', type: 'function', info: 'Round to nearest integer', detail: '(x)' },
    { label: 'max', type: 'function', info: 'Largest of arguments', detail: '(...values)' },
    { label: 'min', type: 'function', info: 'Smallest of arguments', detail: '(...values)' },
    { label: 'random', type: 'function', info: 'Random number 0–1', detail: '()' },
    { label: 'pow', type: 'function', info: 'Base to exponent power', detail: '(base, exponent)' },
    { label: 'sqrt', type: 'function', info: 'Square root', detail: '(x)' },
    { label: 'log', type: 'function', info: 'Natural logarithm', detail: '(x)' },
    { label: 'trunc', type: 'function', info: 'Integer part', detail: '(x)' },
    { label: 'sign', type: 'function', info: 'Sign of number (-1, 0, 1)', detail: '(x)' },
    { label: 'PI', type: 'constant', info: '3.141592653589793' },
    { label: 'E', type: 'constant', info: '2.718281828459045' },
  ],
  Date: [
    { label: 'now', type: 'function', info: 'Current timestamp in ms', detail: '()' },
    { label: 'parse', type: 'function', info: 'Parse date string to ms', detail: '(dateString)' },
    { label: 'UTC', type: 'function', info: 'UTC timestamp in ms', detail: '(year, month, ...)' },
  ],
  Array: [
    { label: 'isArray', type: 'function', info: 'Check if value is array', detail: '(value)' },
    { label: 'from', type: 'function', info: 'Create array from iterable', detail: '(iterable, mapFn?)' },
    { label: 'of', type: 'function', info: 'Create array from arguments', detail: '(...items)' },
  ],
  Object: [
    { label: 'keys', type: 'function', info: 'Get object keys as array', detail: '(obj)' },
    { label: 'values', type: 'function', info: 'Get object values as array', detail: '(obj)' },
    { label: 'entries', type: 'function', info: 'Get [key, value] pairs', detail: '(obj)' },
    { label: 'assign', type: 'function', info: 'Copy properties to target', detail: '(target, ...sources)' },
    { label: 'freeze', type: 'function', info: 'Freeze object (immutable)', detail: '(obj)' },
    { label: 'hasOwn', type: 'function', info: 'Check if object has own property', detail: '(obj, prop)' },
  ],
  String: [
    { label: 'fromCharCode', type: 'function', info: 'String from char codes', detail: '(...codes)' },
  ],
  Number: [
    { label: 'isInteger', type: 'function', info: 'Check if integer', detail: '(value)' },
    { label: 'isFinite', type: 'function', info: 'Check if finite number', detail: '(value)' },
    { label: 'isNaN', type: 'function', info: 'Check if NaN', detail: '(value)' },
    { label: 'parseInt', type: 'function', info: 'Parse string to integer', detail: '(string, radix?)' },
    { label: 'parseFloat', type: 'function', info: 'Parse string to float', detail: '(string)' },
    { label: 'MAX_SAFE_INTEGER', type: 'constant', info: '9007199254740991' },
    { label: 'MIN_SAFE_INTEGER', type: 'constant', info: '-9007199254740991' },
  ],
  console: [
    { label: 'log', type: 'function', info: 'Log to console', detail: '(...args)' },
    { label: 'error', type: 'function', info: 'Log error', detail: '(...args)' },
    { label: 'warn', type: 'function', info: 'Log warning', detail: '(...args)' },
    { label: 'info', type: 'function', info: 'Log info', detail: '(...args)' },
  ],
};

/** Common output properties available on action/trigger outputs */
const outputProperties: Completion[] = [
  { label: 'body', type: 'property', info: 'Response body' },
  { label: 'headers', type: 'property', info: 'Response headers' },
  { label: 'statusCode', type: 'property', info: 'HTTP status code' },
  { label: 'queries', type: 'property', info: 'Query string parameters' },
];

// =============================================================================
// WORKFLOW CONTEXT INFO (dynamic from workflow)
// =============================================================================

/** Describes an action or trigger in the workflow for IntelliSense */
export interface WorkflowActionInfo {
  /** Action name as it appears in the workflow definition */
  name: string;
  /** Display-friendly name */
  displayName?: string;
  /** Whether this is the trigger (vs an action) */
  isTrigger?: boolean;
  /** Known output property names (from manifest outputs schema) */
  outputProperties?: string[];
}

// =============================================================================
// AUTOCOMPLETE FUNCTION
// =============================================================================

/**
 * Returns true when `name` is a valid JavaScript identifier and can be used
 * with dot-notation property access (e.g. `obj.name`).  Names that contain
 * hyphens or other non-identifier characters must use bracket notation instead.
 */
function isValidIdentifier(name: string): boolean {
  return /^[a-zA-Z_$][\w$]*$/.test(name);
}

/**
 * Resolve the object path before the dot to find member completions.
 * Supports both dot-notation and bracket-notation segments so that paths
 * whose action names contain hyphens are also recognised.
 *
 * Examples:
 *   "workflowContext.trigger."               → "workflowContext.trigger"
 *   "workflowContext.actions.validName."     → "workflowContext.actions.validName"
 *   "workflowContext.actions['my-action']."  → "workflowContext.actions['my-action']"
 *   "workflowContext.actions['my-action'].outputs." → "workflowContext.actions['my-action'].outputs"
 */
export function getObjectPath(text: string): string | null {
  // Each segment is either a plain identifier ([\w$]+) or a bracket-notation key (\[['"]...['"]\]).
  // Segments after the first are preceded by a dot.
  const match = text.match(/([\w$]+(?:\.[\w$]+|\[['"][\w$-]+['"]\])*)\.$/);
  return match ? (match[1] ?? null) : null;
}

/**
 * Build the workflowContext member completions dynamically based on
 * the actions and triggers present in the workflow.
 */
export function buildWorkflowContextCompletions(
  actions: WorkflowActionInfo[]
): Record<string, Completion[]> {
  const completions: Record<string, Completion[]> = {
    workflowContext: [
      { label: 'workflow', type: 'property', info: 'Workflow metadata' },
      { label: 'actions', type: 'property', info: 'Action outputs' },
      { label: 'trigger', type: 'property', info: 'Trigger outputs' },
    ],
    'workflowContext.workflow': [
      { label: 'name', type: 'property', info: 'Workflow name' },
      { label: 'id', type: 'property', info: 'Workflow resource ID' },
      { label: 'run', type: 'property', info: 'Current run metadata' },
    ],
    'workflowContext.trigger': [
      { label: 'name', type: 'property', info: 'Trigger name' },
      { label: 'outputs', type: 'property', info: 'Trigger output data' },
    ],
    'workflowContext.trigger.outputs': [...outputProperties],
  };

  // Build action name completions for workflowContext.actions.
  const actionCompletions: Completion[] = [];
  for (const action of actions) {
    if (action.isTrigger) continue;

    // Action names that are not valid JS identifiers (e.g. names containing hyphens)
    // cannot be accessed via dot notation — they require bracket notation.
    const usesBracket = !isValidIdentifier(action.name);

    // The key used in the completions map must match what getObjectPath() returns
    // after the user accepts a completion and then types another dot.
    const pathKey = usesBracket
      ? `workflowContext.actions['${action.name}']`
      : `workflowContext.actions.${action.name}`;

    const completion: Completion = {
      label: action.name,
      type: 'property',
      info: action.displayName ?? action.name,
    };

    if (usesBracket) {
      // Show a hint in the popup so the user knows bracket notation will be inserted.
      const bracketText = `['${action.name}']`;
      completion.detail = bracketText;
      // The apply callback replaces the preceding dot together with any partially-typed
      // text (from..to) with the bracket-notation equivalent.  `from` is the position
      // right after the triggering dot, so `from - 1` is the dot itself.
      completion.apply = (view: EditorView, _c: Completion, from: number, to: number) => {
        view.dispatch({ changes: { from: from - 1, to, insert: bracketText } });
      };
    }

    actionCompletions.push(completion);

    // Each action has .outputs
    completions[pathKey] = [
      { label: 'outputs', type: 'property', info: `Outputs of ${action.displayName ?? action.name}` },
    ];

    // Each action.outputs has body, headers, statusCode
    const actionOutputs = [...outputProperties];
    if (action.outputProperties) {
      for (const prop of action.outputProperties) {
        if (!actionOutputs.some(o => o.label === prop)) {
          actionOutputs.push({ label: prop, type: 'property', info: `Output property` });
        }
      }
    }
    completions[`${pathKey}.outputs`] = actionOutputs;
  }

  completions['workflowContext.actions'] = actionCompletions;

  return completions;
}

/**
 * Create a JavaScript completion source for CodeMirror
 */
function createJsCompletionSource(actions: WorkflowActionInfo[]) {
  const workflowCompletions = buildWorkflowContextCompletions(actions);

  return function jsCompletionSource(context: CompletionContext) {
    const line = context.state.doc.lineAt(context.pos);
    const textBefore = line.text.slice(0, context.pos - line.from);

    // Check if we're completing after a dot (member access)
    const objectPath = getObjectPath(textBefore);
    if (objectPath) {
      // Check static built-in completions (JSON, Math, etc.)
      const staticMembers = staticMemberCompletions[objectPath];
      if (staticMembers) {
        return { from: context.pos, options: staticMembers, validFor: /^\w*$/ };
      }

      // Check dynamic workflowContext completions
      const workflowMembers = workflowCompletions[objectPath];
      if (workflowMembers) {
        return { from: context.pos, options: workflowMembers, validFor: /^\w*$/ };
      }

      // For any *.outputs path we don't have specific info for, suggest common properties
      if (objectPath.endsWith('.outputs')) {
        return { from: context.pos, options: outputProperties, validFor: /^\w*$/ };
      }

      return null;
    }

    // Global completions (typing an identifier)
    const word = context.matchBefore(/\w+/);
    if (!word && !context.explicit) return null;

    return {
      from: word ? word.from : context.pos,
      options: globalCompletions,
      validFor: /^\w*$/,
    };
  };
}

// =============================================================================
// EXTENSION EXPORT
// =============================================================================

/**
 * Create a CodeMirror extension providing JavaScript IntelliSense.
 *
 * @param actions - List of workflow actions/triggers for workflowContext completions.
 *                  Pass an empty array for static-only completions.
 */
export function createJavaScriptAutocomplete(actions: WorkflowActionInfo[] = []): Extension {
  return autocompletion({
    override: [createJsCompletionSource(actions)],
    activateOnTyping: true,
    maxRenderedOptions: 30,
  });
}
